export const languages = [
  { code: "en", label: "EN" },
  { code: "fr", label: "FR" },
  { code: "pt", label: "PT" },
];

export const translations = {
  en: {
    seo: {
      title: "Art Communications for Artists & Cultural Institutions | Camila Valentoni",
      description: "Art communications based in Paris and working internationally with artists, galleries and cultural institutions across press relations, editorial content and digital strategy.",
    },
    altHero: "Artist materials and paint in Xavier Veilhan's studio in Paris",
    altStudioVisit: "Visit to Christian Prunello's artist studio in Paris",
    altGrandPalais: "Art Basel Paris 2025 at the Grand Palais",
    altGallery: "Curiosity exhibition at Galerie Horna in Paris, 2024",
    altOpening: "Opening of the Curiosity exhibition at Galerie Horna in Paris, 2024",
    capHero: "Xavier Veilhan's Studio, Paris, 2026 — © Camila Valentoni",
    capStudioVisit: "Christian Prunello's Studio, Paris, 2026 — © Camila Valentoni",
    capGrandPalais: "Art Basel Paris, 2025 — © Camila Valentoni",
    capGallery: "“Curiosity”, Galerie Horna, Paris, 2024 — © Camila Valentoni",
    capOpening: "Opening of “Curiosity”, Galerie Horna, Paris, 2024 — © Camila Valentoni",
    nav: {
      home: "Home",
      about: "About",
      approach: "Approach",
      contribute: "Working together",
      experience: "Experience",
      contact: "Contact",
    },
    hero: {
      kicker: "Communication for contemporary art",
      line1: "Communication that",
      line2: "extends the",
      line3: "artist’s voice.",
      sub: "I work alongside artists to build narratives that stay true to their practice and find the audiences they deserve.",
      cta: "Start a conversation",
    },
    marquee: [
      "Mediation, not promotion",
      "The artist’s voice, amplified",
      "Press · Editorial · Digital",
      "One story, every channel",
      "An international practice",
    ],
    press: {
      label: "Selected media coverage",
      outlets: ["The Art Newspaper", "Le Temps", "France Culture", "Le Nouvel Obs", "Vogue Brasil", "Forbes Brasil", "Folha de S.Paulo"],
    },
    manifesto: "Every artist already has a language. My role is to help that language reach the audiences it deserves.",
    photoNote: "A photograph from my practice will live here",
    photoCredit: "© Camila Valentoni, 2026",
    photoCredit2024: "© Camila Valentoni, 2024",
    photoCredit2025: "© Camila Valentoni, 2025",
    about: {
      label: "About",
      heading: "I understand the life of an artwork beyond the studio.",
      p1: "An artwork doesn't stop at the studio door. It moves through exhibitions, institutions, collections, publications and public conversations before reaching its audiences. My role is to help that journey remain coherent, so every encounter reflects the artist's voice.",
      p2: "My path has taken me through museums, artist studios, galleries, private collections and cultural institutions in Brazil, Europe and the United States, while collaborating on international projects across museums, galleries and institutions. Working internationally has taught me how to translate artistic practices across cultures without losing their specificity.",
      p3: "I've worked from every side of that conversation, supporting institutions, collaborating with galleries, responding to journalists and developing narratives alongside artists.",
      quote: "That perspective shapes how I approach communication today: not as promotion, but as mediation.",
      signoff: "Camila",
    },
    approach: {
      label: "Approach",
      heading: "Communication is not marketing.",
      chapters: [
        {
          title: "The artist already has a language",
          body: "Every artist develops a singular vocabulary over years of practice. My role is never to replace it with a brand voice. It is to listen closely, understand it deeply, and amplify it.",
        },
        {
          title: "Communication begins before the opening",
          body: "Long before an exhibition opens, the story is already taking shape. Strategy, timing and narrative built early are what allow a project to resonate far beyond its walls.",
        },
        {
          title: "One story, every channel",
          body: "Press, editorial content, partnerships, digital communication and audience development are not separate tasks. They are chapters of the same story, and they must be told in the same voice.",
        },
        {
          title: "Complexity, without simplification",
          body: "I translate complex artistic ideas into engaging narratives without flattening them. Rigorous communication respects both the work and the intelligence of its audiences, and adapts to each cultural context.",
        },
      ],
    },
    contribute: {
      label: "Working together",
      heading: "Every collaboration begins inside your practice.",
      p1: "No formulas, no templates. Our work starts with time spent with your work: your references, your rhythm, your way of speaking about what you make. It takes shape around the artist and the project, never the other way round.",
      situationsLabel: "Ways we can work together",
      situationQuestions: [
        "Preparing for an art fair?",
        "Opening a solo exhibition or launching a major project?",
        "Giving a lecture or participating in a conference?",
        "Need help with day-to-day communication?",
        "Want to develop your digital presence?",
        "Want to strengthen the relationship with your community?",
        "Are your professional materials up to date?",
        "Need to organise and manage your image archive?",
        "Want to keep track of your media coverage?",
        "Need to protect your image rights?",
        "How do you build a long-term practice?",
        "Looking for funding?",
        "Working internationally?",
      ],
      situations: [
        { q: "Preparing for an art fair", a: "Prepare short videos, texts and contextual material that galleries can share with collectors before they arrive." },
        { q: "Opening a solo exhibition or launching a major project", a: "Develop a press release, identify the journalists and institutions who should hear about it, and coordinate media requests around your project." },
        { q: "Giving a lecture or participating in a conference", a: "Shape the visuals, narrative and supporting material so your ideas are communicated as clearly as your work." },
        { q: "Managing day-to-day communication", a: "I'll coordinate media requests, image permissions, invitations and institutional correspondence so you can stay focused on your practice." },
        { q: "Developing your digital presence", a: "Together, we'll shape an online presence that reflects your practice through thoughtful social media, editorial content and a coherent digital identity." },
        { q: "Managing your community", a: "I'll help maintain a communication rhythm that fits your practice, whether through newsletters, announcements or other touchpoints with your audience." },
        { q: "Keeping your professional material up to date", a: "Keep your portfolio, biography, CV and project texts current, so they're always ready whenever opportunities arise." },
        { q: "Managing your image archive", a: "Ensure every exhibition, installation and publication is documented by following up with photographers and institutions and maintaining an organised image bank." },
        { q: "Keeping track of your media coverage", a: "Monitor articles, interviews and mentions over time, creating a media report that's always ready to share with galleries, institutions and collectors." },
        { q: "Protecting your image rights", a: "Monitor unauthorised uses of your images and coordinate follow-up with the relevant rights management organisations." },
        { q: "Building a long-term practice", a: "Organise your archive of texts, photographs and documentation so your work remains accessible and visible over time." },
        { q: "Looking for funding", a: "Prepare thoughtful material for grants, sponsors, patrons or crowdfunding campaigns that remains faithful to your practice." },
        { q: "Working internationally", a: "Adapt your communication across languages, institutions and cultural contexts while maintaining one coherent voice." },
      ],
      principlesLabel: "Three principles",
      principles: [
        { title: "Every artist already has a language", body: "My role is to amplify it, never to replace it. The words we find together should sound like you, not like an agency." },
        { title: "Visibility begins long before the opening", body: "Narrative, timing and relationships are built months ahead. By the time the doors open, the story is already moving." },
        { title: "Tailored, always", body: "Each collaboration is shaped around the artist and the project. Some last a season, others become long-term dialogues." },
      ],
    },
    network: {
      label: "International network",
      heading: "Visibility travels through relationships.",
      intro: "Over the years, I have built an international professional network across the art world, and I keep expanding it, project by project.",
      stats: [
        { value: "1,000+", label: "galleries" },
        { value: "1,100+", label: "cultural institutions" },
        { value: "700+", label: "curators" },
        { value: "500+", label: "collectors & patrons" },
        { value: "1,200+", label: "media contacts" },
      ],
      closing: "I actively research and build new relationships for every project: a living practice, not a static database.",
      geoLabel: "Geographic distribution",
      geoIntro: "A network rooted in France, extending internationally.",
      regions: [
        { name: "France", value: 431 },
        { name: "Rest of Europe", value: 208 },
        { name: "North America", value: 122 },
        { name: "Asia", value: 94 },
        { name: "Latin America", value: 61 },
        { name: "Other regions", value: 9 },
      ],
      countries: "France 431 · United States 119 · South Korea 38 · United Kingdom 36 · Japan 27 · China 26 · Brazil 26 · Italy 25 · Switzerland 23 · Germany 23 · Spain 22 · Mexico 20 · Sweden 18 · Portugal 16 · Argentina 13 · Belgium 12",
      countriesNote: "Additional contacts distributed across other countries.",
      geoNote: "Geographic distribution based on database contacts with an identified country.",
    },
    experience: {
      label: "Experience",
      heading: "A growing practice.",
      types: ["Artist studios", "Museums", "Galleries", "Private collections", "International projects"],
      featured: "Featured project: Transatlantic Studio, with international press coverage and more than 550,000 organic accounts reached on Instagram.",
      note: "Case studies and photographs will join this page over time.",
    },
    contact: {
      label: "Let’s talk",
      heading: "Let’s start a conversation.",
      body: "Every artist and every project is different. If my approach resonates with you, I’d be delighted to hear about your work, and to explore together how we might collaborate.",
      nameLabel: "Name",
      emailLabel: "Email",
      messageLabel: "Message",
      messagePlaceholder: "Tell me about your work, your practice, your project…",
      submit: "Send message",
      sending: "Sending…",
      success: "Thank you, your message has been sent. I will reply soon.",
      error: "Something went wrong. Please write to me directly by email.",
      directLabel: "Or write directly",
      email: "camila@camilavalentoni.com",
      emailLinkText: "Send me an email →",
      footerLine: "Communication for contemporary art, working internationally",
      rights: "All rights reserved",
    },
  },
  fr: {
    seo: {
      title: "Communication pour artistes & institutions culturelles | Camila Valentoni",
      description: "Communication artistique basée à Paris et active à l’international auprès d’artistes, de galeries et d’institutions culturelles : relations presse, contenu éditorial et stratégie digitale.",
    },
    altHero: "Matériel d’artiste et peinture dans l’atelier de Xavier Veilhan à Paris",
    altStudioVisit: "Visite de l’atelier de l’artiste Christian Prunello à Paris",
    altGrandPalais: "Art Basel Paris 2025 au Grand Palais",
    altGallery: "Exposition Curiosity à la Galerie Horna à Paris, 2024",
    altOpening: "Vernissage de l’exposition Curiosity à la Galerie Horna à Paris, 2024",
    capHero: "Atelier de Xavier Veilhan, Paris, 2026 — © Camila Valentoni",
    capStudioVisit: "Atelier de Christian Prunello, Paris, 2026 — © Camila Valentoni",
    capGrandPalais: "Art Basel Paris, 2025 — © Camila Valentoni",
    capGallery: "« Curiosity », Galerie Horna, Paris, 2024 — © Camila Valentoni",
    capOpening: "Vernissage de « Curiosity », Galerie Horna, Paris, 2024 — © Camila Valentoni",
    nav: {
      home: "Accueil",
      about: "À propos",
      approach: "Approche",
      contribute: "Travailler ensemble",
      experience: "Expérience",
      contact: "Contact",
    },
    hero: {
      kicker: "Communication pour l’art contemporain",
      line1: "Une communication",
      line2: "qui prolonge",
      line3: "la voix de l’artiste.",
      sub: "Je travaille aux côtés des artistes pour construire des récits fidèles à leur pratique et capables de trouver leur public.",
      cta: "Échangeons",
    },
    marquee: [
      "La médiation, pas la promotion",
      "La voix de l’artiste, amplifiée",
      "Presse · Éditorial · Digital",
      "Un récit, tous les canaux",
      "Une pratique internationale",
    ],
    press: {
      label: "Couverture médiatique sélectionnée",
      intro: "Une sélection de publications ayant couvert des projets auxquels j’ai contribué à travers la communication stratégique.",
      outlets: ["The Art Newspaper", "Le Temps", "France Culture", "Le Nouvel Obs", "Vogue Brasil", "Forbes Brasil", "Folha de S.Paulo"],
    },
    manifesto: "Chaque artiste porte déjà un langage. Mon rôle est d’aider ce langage à atteindre les publics qu’il mérite.",
    photoNote: "Une photographie de ma pratique trouvera sa place ici",
    photoCredit: "© Camila Valentoni, 2026",
    photoCredit2024: "© Camila Valentoni, 2024",
    photoCredit2025: "© Camila Valentoni, 2025",
    about: {
      label: "À propos",
      heading: "Je comprends la vie d’une œuvre au-delà de l’atelier.",
      p1: "Une œuvre ne s’arrête pas à la porte de l’atelier. Elle circule à travers les expositions, les institutions, les collections, les publications et les conversations publiques avant de rencontrer ses publics. Mon rôle est d’accompagner ce parcours pour qu’il reste cohérent, afin que chaque rencontre reflète la voix de l’artiste.",
      p2: "Mon parcours m’a menée à travers des musées, des ateliers d’artistes, des galeries, des collections privées et des institutions culturelles, au Brésil, en Europe et aux États-Unis, tout en collaborant à des projets internationaux avec musées, galeries et institutions. Travailler à l’international m’a appris à traduire les pratiques artistiques d’une culture à l’autre sans en perdre la spécificité.",
      p3: "J’ai travaillé de tous les côtés de cette conversation : en soutenant des institutions, en collaborant avec des galeries, en répondant aux journalistes et en développant des récits aux côtés des artistes.",
      quote: "Cette perspective façonne ma manière d’aborder la communication aujourd’hui : non comme une promotion, mais comme une médiation.",
      signoff: "Camila",
    },
    approach: {
      label: "Approche",
      heading: "La communication n’est pas du marketing.",
      chapters: [
        {
          title: "L’artiste a déjà un langage",
          body: "Chaque artiste développe un vocabulaire singulier au fil des années de pratique. Mon rôle n’est jamais de le remplacer par une voix de marque. C’est de l’écouter attentivement, de le comprendre profondément et de l’amplifier.",
        },
        {
          title: "La communication commence avant le vernissage",
          body: "Bien avant l’ouverture d’une exposition, le récit prend déjà forme. La stratégie, le rythme et la narration construits en amont permettent à un projet de résonner bien au-delà de ses murs.",
        },
        {
          title: "Un récit, tous les canaux",
          body: "Presse, contenus éditoriaux, partenariats, communication digitale et développement des publics ne sont pas des tâches séparées. Ce sont les chapitres d’un même récit, qui doit être raconté d’une seule voix.",
        },
        {
          title: "La complexité, sans simplification",
          body: "Je traduis des idées artistiques complexes en récits captivants sans les aplatir. Une communication exigeante respecte l’œuvre et l’intelligence de ses publics, et s’adapte à chaque contexte culturel.",
        },
      ],
    },
    contribute: {
      label: "Travailler ensemble",
      heading: "Chaque collaboration commence au cœur de votre pratique.",
      p1: "Ni formules ni modèles. Notre travail commence par du temps passé avec votre œuvre : vos références, votre rythme, votre manière de dire ce que vous faites. Il se façonne autour de l’artiste et du projet, jamais l’inverse.",
      situationsLabel: "Les façons de travailler ensemble",
      situationQuestions: [
        "Vous préparez une foire d’art ?",
        "Vous ouvrez une exposition personnelle ou lancez un projet majeur ?",
        "Vous donnez une conférence ou participez à une table ronde ?",
        "Besoin d’aide pour la communication au quotidien ?",
        "Envie de développer votre présence en ligne ?",
        "Envie de renforcer le lien avec votre communauté ?",
        "Vos supports professionnels sont-ils à jour ?",
        "Besoin d’organiser et de gérer votre archive d’images ?",
        "Envie de suivre votre couverture médiatique ?",
        "Besoin de protéger vos droits d’image ?",
        "Comment construire une pratique sur le long terme ?",
        "Vous cherchez des financements ?",
        "Travailler à l’international ?",
      ],
      situations: [
        { q: "Vous préparez une foire d’art", a: "Préparer de courtes vidéos, des textes et du matériel contextuel que les galeries peuvent partager avec les collectionneurs avant leur arrivée." },
        { q: "Vous ouvrez une exposition personnelle ou lancez un projet majeur", a: "Rédiger un communiqué de presse, identifier les journalistes et les institutions qui doivent en entendre parler, et coordonner les demandes des médias autour de votre projet." },
        { q: "Vous donnez une conférence ou participez à une table ronde", a: "Façonner les visuels, le récit et les supports pour que vos idées soient communiquées aussi clairement que votre travail." },
        { q: "Gérer la communication au quotidien", a: "Je coordonne les demandes des médias, les autorisations d’images, les invitations et la correspondance avec les institutions, afin que vous puissiez vous concentrer sur votre pratique." },
        { q: "Développer votre présence en ligne", a: "Ensemble, nous façonnons une présence en ligne qui reflète votre pratique : réseaux sociaux soignés, contenus éditoriaux et identité numérique cohérente." },
        { q: "Gérer votre communauté", a: "Je vous aide à maintenir un rythme de communication adapté à votre pratique, que ce soit par des newsletters, des annonces ou d’autres points de contact avec votre public." },
        { q: "Garder vos supports professionnels à jour", a: "Garder à jour votre portfolio, votre biographie, votre CV et vos textes de projet, pour qu’ils soient toujours prêts lorsqu’une opportunité se présente." },
        { q: "Gérer votre archive d’images", a: "Veiller à ce que chaque exposition, installation et publication soit documentée, en assurant le suivi avec les photographes et les institutions et en maintenant une banque d’images organisée." },
        { q: "Suivre votre couverture médiatique", a: "Suivre articles, interviews et mentions dans le temps, en constituant un bilan presse toujours prêt à partager avec galeries, institutions et collectionneurs." },
        { q: "Protéger vos droits d’image", a: "Surveiller les utilisations non autorisées de vos images et coordonner le suivi avec les organismes de gestion des droits, comme l’ADAGP." },
        { q: "Construire une pratique sur le long terme", a: "Organiser votre archive de textes, de photographies et de documentation, pour que votre travail reste accessible et visible dans le temps." },
        { q: "Chercher des financements", a: "Préparer des supports soignés pour les bourses, mécènes, sponsors ou campagnes de financement participatif, fidèles à votre pratique." },
        { q: "Travailler à l’international", a: "Adapter votre communication entre langues, institutions et contextes culturels, en maintenant une voix cohérente." },
      ],
      principlesLabel: "Trois principes",
      principles: [
        { title: "Chaque artiste a déjà un langage", body: "Mon rôle est de l’amplifier, jamais de le remplacer. Les mots que nous trouvons ensemble doivent sonner comme vous, pas comme une agence." },
        { title: "La visibilité se construit bien avant le vernissage", body: "Récit, rythme et relations se bâtissent des mois en amont. Lorsque les portes s’ouvrent, l’histoire est déjà en mouvement." },
        { title: "Sur mesure, toujours", body: "Chaque collaboration se façonne autour de l’artiste et du projet. Certaines durent une saison, d’autres deviennent des dialogues de long terme." },
      ],
    },
    network: {
      label: "Réseau international",
      heading: "La visibilité circule par les relations.",
      intro: "Au fil des années, j’ai construit un réseau professionnel international dans le monde de l’art, que je continue d’élargir, projet après projet.",
      stats: [
        { value: "1 000+", label: "galeries" },
        { value: "1 100+", label: "institutions culturelles" },
        { value: "700+", label: "curateurs" },
        { value: "500+", label: "collectionneurs & mécènes" },
        { value: "1 200+", label: "contacts médias" },
      ],
      closing: "Je recherche et construis activement de nouvelles relations pour chaque projet : une pratique vivante, pas une base de données figée.",
      geoLabel: "Distribution géographique",
      geoIntro: "Un réseau ancré en France, étendu à l’international.",
      regions: [
        { name: "France", value: 431 },
        { name: "Reste de l’Europe", value: 208 },
        { name: "Amérique du Nord", value: 122 },
        { name: "Asie", value: 94 },
        { name: "Amérique latine", value: 61 },
        { name: "Autres régions", value: 9 },
      ],
      countries: "France 431 · États-Unis 119 · Corée du Sud 38 · Royaume-Uni 36 · Japon 27 · Chine 26 · Brésil 26 · Italie 25 · Suisse 23 · Allemagne 23 · Espagne 22 · Mexique 20 · Suède 18 · Portugal 16 · Argentine 13 · Belgique 12",
      countriesNote: "Contacts supplémentaires répartis dans d’autres pays.",
      geoNote: "Distribution géographique établie à partir des contacts de la base de données dont le pays est identifié.",
    },
    experience: {
      label: "Expérience",
      heading: "Une pratique en croissance.",
      types: ["Ateliers d’artistes", "Musées", "Galeries", "Collections privées", "Projets internationaux"],
      featured: "Projet phare : Transatlantic Studio, avec une couverture presse internationale et plus de 550 000 comptes atteints de manière organique sur Instagram.",
      note: "Études de cas et photographies rejoindront cette page au fil du temps.",
    },
    contact: {
      label: "Parlons-en",
      heading: "Commençons une conversation.",
      body: "Chaque artiste et chaque projet est différent. Si mon approche résonne avec vous, je serais ravie de découvrir votre travail, et d’explorer ensemble comment nous pourrions collaborer.",
      nameLabel: "Nom",
      emailLabel: "E-mail",
      messageLabel: "Message",
      messagePlaceholder: "Parlez-moi de votre travail, de votre pratique, de votre projet…",
      submit: "Envoyer le message",
      sending: "Envoi…",
      success: "Merci, votre message a bien été envoyé. Je vous répondrai très vite.",
      error: "Une erreur est survenue. Écrivez-moi directement par e-mail.",
      directLabel: "Ou écrivez directement",
      email: "camila@camilavalentoni.com",
      emailLinkText: "Envoyez-moi un e-mail →",
      footerLine: "Communication pour l’art contemporain, à l’international",
      rights: "Tous droits réservés",
    },
  },
  pt: {
    seo: {
      title: "Comunicação para artistas e instituições culturais | Camila Valentoni",
      description: "Comunicação de arte baseada em Paris e com atuação internacional junto a artistas, galerias e instituições culturais em relações de imprensa, conteúdo editorial e estratégia digital.",
    },
    altHero: "Materiais de artista e tintas no ateliê de Xavier Veilhan em Paris",
    altStudioVisit: "Visita ao ateliê do artista Christian Prunello em Paris",
    altGrandPalais: "Art Basel Paris 2025 no Grand Palais",
    altGallery: "Exposição Curiosity na Galerie Horna em Paris, 2024",
    altOpening: "Abertura da exposição Curiosity na Galerie Horna em Paris, 2024",
    capHero: "Ateliê de Xavier Veilhan, Paris, 2026 — © Camila Valentoni",
    capStudioVisit: "Ateliê de Christian Prunello, Paris, 2026 — © Camila Valentoni",
    capGrandPalais: "Art Basel Paris, 2025 — © Camila Valentoni",
    capGallery: "“Curiosity”, Galerie Horna, Paris, 2024 — © Camila Valentoni",
    capOpening: "Abertura de “Curiosity”, Galerie Horna, Paris, 2024 — © Camila Valentoni",
    nav: {
      home: "Início",
      about: "Sobre",
      approach: "Abordagem",
      contribute: "Trabalhar juntos",
      experience: "Experiência",
      contact: "Contato",
    },
    hero: {
      kicker: "Comunicação para a arte contemporânea",
      line1: "Comunicação que",
      line2: "prolonga a",
      line3: "voz do artista.",
      sub: "Trabalho ao lado de artistas para construir narrativas fiéis à sua prática e capazes de encontrar seu público.",
      cta: "Começar uma conversa",
    },
    marquee: [
      "Mediação, não promoção",
      "A voz do artista, amplificada",
      "Imprensa · Editorial · Digital",
      "Uma história, todos os canais",
      "Atuação internacional",
    ],
    press: {
      label: "Cobertura de mídia selecionada",
      outlets: ["The Art Newspaper", "Le Temps", "France Culture", "Le Nouvel Obs", "Vogue Brasil", "Forbes Brasil", "Folha de S.Paulo"],
    },
    manifesto: "Todo artista já carrega uma linguagem. Meu papel é ajudar essa linguagem a alcançar os públicos que ela merece.",
    photoNote: "Uma fotografia da minha prática viverá aqui",
    photoCredit: "© Camila Valentoni, 2026",
    photoCredit2024: "© Camila Valentoni, 2024",
    photoCredit2025: "© Camila Valentoni, 2025",
    about: {
      label: "Sobre",
      heading: "Eu entendo a vida de uma obra para além do ateliê.",
      p1: "Uma obra não para na porta do ateliê. Ela circula por exposições, instituições, coleções, publicações e conversas públicas antes de alcançar seus públicos. Meu papel é ajudar esse percurso a permanecer coerente, para que cada encontro reflita a voz do artista.",
      p2: "Meu percurso me levou por museus, ateliês de artistas, galerias, coleções privadas e instituições culturais no Brasil, na Europa e nos Estados Unidos, enquanto colaborava em projetos internacionais com museus, galerias e instituições. Trabalhar internacionalmente me ensinou a traduzir práticas artísticas entre culturas sem perder sua especificidade.",
      p3: "Trabalhei de todos os lados dessa conversa: apoiando instituições, colaborando com galerias, respondendo a jornalistas e desenvolvendo narrativas ao lado de artistas.",
      quote: "Essa perspectiva define como encaro a comunicação hoje: não como promoção, mas como mediação.",
      signoff: "Camila",
    },
    approach: {
      label: "Abordagem",
      heading: "Comunicação não é marketing.",
      chapters: [
        {
          title: "O artista já tem uma linguagem",
          body: "Cada artista desenvolve um vocabulário singular ao longo de anos de prática. Meu papel nunca é substituí-la por uma voz de marca. É ouvi-la atentamente, compreendê-la profundamente e amplificá-la.",
        },
        {
          title: "A comunicação começa antes da abertura",
          body: "Muito antes da inauguração de uma exposição, a narrativa já está tomando forma. Estratégia, ritmo e narrativa construídos cedo são o que permite a um projeto ressoar muito além de suas paredes.",
        },
        {
          title: "Uma história, todos os canais",
          body: "Imprensa, conteúdo editorial, parcerias, comunicação digital e desenvolvimento de públicos não são tarefas separadas. São capítulos da mesma história, e devem ser contados com a mesma voz.",
        },
        {
          title: "Complexidade, sem simplificação",
          body: "Traduzo ideias artísticas complexas em narrativas envolventes sem achatá-las. Uma comunicação rigorosa respeita a obra e a inteligência de seus públicos, e se adapta a cada contexto cultural.",
        },
      ],
    },
    contribute: {
      label: "Trabalhar juntos",
      heading: "Cada colaboração começa dentro da sua prática.",
      p1: "Sem fórmulas, sem modelos. Nosso trabalho começa com tempo dedicado à sua obra: suas referências, seu ritmo, sua maneira de falar sobre o que você cria. Ele toma forma em torno do artista e do projeto, nunca o contrário.",
      situationsLabel: "Formas de trabalharmos juntos",
      situationQuestions: [
        "Preparando-se para uma feira de arte?",
        "Abrindo uma exposição individual ou lançando um grande projeto?",
        "Dando uma palestra ou participando de uma conferência?",
        "Precisa de ajuda com a comunicação do dia a dia?",
        "Quer desenvolver sua presença digital?",
        "Quer fortalecer a relação com sua comunidade?",
        "Seus materiais profissionais estão atualizados?",
        "Precisa organizar e gerenciar seu arquivo de imagens?",
        "Quer acompanhar sua cobertura de mídia?",
        "Precisa proteger seus direitos de imagem?",
        "Como construir uma prática de longo prazo?",
        "Buscando financiamento?",
        "Quer trabalhar internacionalmente?",
      ],
      situations: [
        { q: "Preparando-se para uma feira de arte", a: "Preparar vídeos curtos, textos e material contextual que as galerias possam compartilhar com colecionadores antes da chegada deles." },
        { q: "Abrindo uma exposição individual ou lançando um grande projeto", a: "Desenvolver um press release, identificar os jornalistas e as instituições que devem saber disso, e coordenar os pedidos da mídia em torno do seu projeto." },
        { q: "Dando uma palestra ou participando de uma conferência", a: "Dar forma aos visuais, à narrativa e aos materiais de apoio para que suas ideias sejam comunicadas com a mesma clareza do seu trabalho." },
        { q: "Gerenciando a comunicação do dia a dia", a: "Eu coordeno os pedidos da mídia, as autorizações de imagem, os convites e a correspondência institucional, para que você possa focar na sua prática." },
        { q: "Desenvolvendo sua presença digital", a: "Juntos, vamos construir uma presença online que reflita sua prática: redes sociais cuidadosas, conteúdo editorial e uma identidade digital coerente." },
        { q: "Gerenciando sua comunidade", a: "Eu ajudo a manter um ritmo de comunicação que combina com a sua prática, seja por newsletters, anúncios ou outros pontos de contato com seu público." },
        { q: "Mantendo seu material profissional atualizado", a: "Manter seu portfólio, biografia, CV e textos de projeto atualizados, sempre prontos para quando surgirem oportunidades." },
        { q: "Gerenciando seu arquivo de imagens", a: "Garantir que cada exposição, instalação e publicação seja documentada, acompanhando fotógrafos e instituições e mantendo um banco de imagens organizado." },
        { q: "Acompanhando sua cobertura de mídia", a: "Monitorar artigos, entrevistas e menções ao longo do tempo, criando um relatório de mídia sempre pronto para compartilhar com galerias, instituições e colecionadores." },
        { q: "Protegendo seus direitos de imagem", a: "Monitorar usos não autorizados das suas imagens e coordenar o acompanhamento com os organismos de gestão de direitos correspondentes." },
        { q: "Construindo uma prática de longo prazo", a: "Organizar seu arquivo de textos, fotografias e documentação, para que seu trabalho permaneça acessível e visível ao longo do tempo." },
        { q: "Buscando financiamento", a: "Preparar materiais cuidadosos para bolsas, patrocinadores, mecenas ou campanhas de financiamento coletivo, fiéis à sua prática." },
        { q: "Trabalhando internacionalmente", a: "Adaptar sua comunicação entre idiomas, instituições e contextos culturais, mantendo uma voz coerente." },
      ],
      principlesLabel: "Três princípios",
      principles: [
        { title: "Todo artista já tem uma linguagem", body: "Meu papel é amplificá-la, nunca substituí-la. As palavras que encontramos juntos devem soar como você, não como uma agência." },
        { title: "A visibilidade começa muito antes da abertura", body: "Narrativa, ritmo e relações são construídos com meses de antecedência. Quando as portas se abrem, a história já está em movimento." },
        { title: "Sob medida, sempre", body: "Cada colaboração toma forma em torno do artista e do projeto. Algumas duram uma temporada, outras se tornam diálogos de longo prazo." },
      ],
    },
    network: {
      label: "Rede internacional",
      heading: "A visibilidade circula através de relações.",
      intro: "Ao longo dos anos, construí uma rede profissional internacional no mundo da arte, que continuo expandindo, projeto a projeto.",
      stats: [
        { value: "1.000+", label: "galerias" },
        { value: "1.100+", label: "instituições culturais" },
        { value: "700+", label: "curadores" },
        { value: "500+", label: "colecionadores & mecenas" },
        { value: "1.200+", label: "contatos de mídia" },
      ],
      closing: "Pesquiso e construo ativamente novas relações para cada projeto: uma prática viva, não um banco de dados estático.",
      geoLabel: "Distribuição geográfica",
      geoIntro: "Uma rede enraizada na França, estendida internacionalmente.",
      regions: [
        { name: "França", value: 431 },
        { name: "Restante da Europa", value: 208 },
        { name: "América do Norte", value: 122 },
        { name: "Ásia", value: 94 },
        { name: "América Latina", value: 61 },
        { name: "Outras regiões", value: 9 },
      ],
      countries: "França 431 · Estados Unidos 119 · Coreia do Sul 38 · Reino Unido 36 · Japão 27 · China 26 · Brasil 26 · Itália 25 · Suíça 23 · Alemanha 23 · Espanha 22 · México 20 · Suécia 18 · Portugal 16 · Argentina 13 · Bélgica 12",
      countriesNote: "Contatos adicionais distribuídos por outros países.",
      geoNote: "Distribuição geográfica baseada nos contatos da base de dados com país identificado.",
    },
    experience: {
      label: "Experiência",
      heading: "Uma prática em crescimento.",
      types: ["Ateliês de artistas", "Museus", "Galerias", "Coleções privadas", "Projetos internacionais"],
      featured: "Projeto em destaque: Transatlantic Studio, com cobertura internacional de imprensa e mais de 550 mil contas alcançadas organicamente no Instagram.",
      note: "Estudos de caso e fotografias chegarão a esta página com o tempo.",
    },
    contact: {
      label: "Vamos conversar",
      heading: "Vamos começar uma conversa.",
      body: "Cada artista e cada projeto é diferente. Se a minha abordagem ressoar com você, será um prazer conhecer seu trabalho, e explorar juntos como podemos colaborar.",
      nameLabel: "Nome",
      emailLabel: "E-mail",
      messageLabel: "Mensagem",
      messagePlaceholder: "Conte-me sobre seu trabalho, sua prática, seu projeto…",
      submit: "Enviar mensagem",
      sending: "Enviando…",
      success: "Obrigada, sua mensagem foi enviada. Responderei em breve.",
      error: "Algo deu errado. Escreva-me diretamente por e-mail.",
      directLabel: "Ou escreva diretamente",
      email: "camila@camilavalentoni.com",
      emailLinkText: "Envie-me um e-mail →",
      footerLine: "Comunicação para a arte contemporânea, com atuação internacional",
      rights: "Todos os direitos reservados",
    },
  },
};
